"use client";

import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutGrid,
  Rows3,
  Columns3,
  Grid2X2,
  Grid3X3,
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  Palette,
  Settings2,
  Square,
  Layers,
  Minus,
  Box,
  Calculator,
  Folder,
  FileText,
  User,
  Package,
  Settings,
  Briefcase,
  Building2,
  Truck,
  CreditCard,
  Calendar,
  MapPin,
  Tag,
  BarChart3,
  PieChart,
  TrendingUp,
  Percent,
  DollarSign,
  Euro,
  Hash,
  Clock,
  Shield,
  Zap,
  Target,
  Award,
  Star,
  Heart,
  Flag,
  Bookmark,
  Monitor,
  Smartphone,
  Maximize2,
  Minimize2,
  MoreHorizontal,
  Play,
  RotateCcw,
  type LucideIcon,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  listAllFormFieldGroups,
  updateFormFieldGroup,
} from "@/lib/api/form-fields";
import {
  listAllOutputFieldGroups,
  updateOutputFieldGroup,
} from "@/lib/api/output-fields";
import { getVersion, updateVersion } from "@/lib/api/versions";
import { getErrorMessage } from "@/lib/api/client";
import type { 
  FormFieldGroupWithFields,
  OutputFieldGroupWithFields,
  FormField,
  OutputField,
  GroupLayout,
  GroupCardStyle,
  GroupLayoutOptions,
  AppearanceOptions,
  FormGroupsPerRow,
  OutputGroupsPerRow,
  ResultsPosition,
  ResultsWidth,
  AttachmentsPosition,
  ChildrenPerRow,
  RadioLayout,
} from "@/types";

interface AppearanceTabProps {
  versionId: number;
  simulatorId: number;
  isEditable: boolean;
}

const layoutOptions: { value: GroupLayout; label: string; icon: React.ReactNode; description: string }[] = [
  { 
    value: "vertical", 
    label: "Vertical", 
    icon: <Rows3 className="h-4 w-4" />,
    description: "Champs empilés verticalement"
  },
  { 
    value: "horizontal", 
    label: "Horizontal", 
    icon: <Columns3 className="h-4 w-4" />,
    description: "Champs côte à côte"
  },
  { 
    value: "grid-2", 
    label: "Grille 2 colonnes", 
    icon: <Grid2X2 className="h-4 w-4" />,
    description: "2 champs par ligne"
  },
  { 
    value: "grid-3", 
    label: "Grille 3 colonnes", 
    icon: <Grid3X3 className="h-4 w-4" />,
    description: "3 champs par ligne"
  },
  { 
    value: "grid-4", 
    label: "Grille 4 colonnes", 
    icon: <LayoutGrid className="h-4 w-4" />,
    description: "4 champs par ligne"
  },
  { 
    value: "grid-5", 
    label: "Grille 5 colonnes", 
    icon: <LayoutGrid className="h-4 w-4" />,
    description: "5 champs par ligne"
  },
];

const cardStyleOptions: { value: GroupCardStyle; label: string; icon: React.ReactNode; description: string }[] = [
  { 
    value: "default", 
    label: "Par défaut", 
    icon: <Square className="h-4 w-4" />,
    description: "Style standard"
  },
  { 
    value: "bordered", 
    label: "Bordé", 
    icon: <Box className="h-4 w-4" />,
    description: "Avec bordure visible"
  },
  { 
    value: "elevated", 
    label: "Élevé", 
    icon: <Layers className="h-4 w-4" />,
    description: "Avec ombre portée"
  },
  { 
    value: "minimal", 
    label: "Minimal", 
    icon: <Minus className="h-4 w-4" />,
    description: "Sans bordure ni fond"
  },
];

// Global appearance options
const formGroupsPerRowOptions: { value: FormGroupsPerRow; label: string; description: string }[] = [
  { value: 1, label: "1 groupe par ligne", description: "Groupes empilés verticalement" },
  { value: 2, label: "2 groupes par ligne", description: "Groupes côte à côte (2 colonnes)" },
  { value: 3, label: "3 groupes par ligne", description: "Groupes en grille (3 colonnes)" },
];

const outputGroupsPerRowOptions: { value: OutputGroupsPerRow; label: string; description: string }[] = [
  { value: 1, label: "1 groupe par ligne", description: "Groupes empilés verticalement" },
  { value: 2, label: "2 groupes par ligne", description: "Groupes côte à côte (2 colonnes)" },
  { value: 3, label: "3 groupes par ligne", description: "Groupes en grille (3 colonnes)" },
];

const childrenPerRowOptions: { value: ChildrenPerRow; label: string; description: string }[] = [
  { value: 1, label: "1 par ligne", description: "Groupes enfants empilés verticalement" },
  { value: 2, label: "2 par ligne", description: "2 groupes enfants côte à côte" },
  { value: 3, label: "3 par ligne", description: "3 groupes enfants par ligne" },
];

const radioLayoutOptions: {
  value: RadioLayout | undefined;
  label: string;
  icon: React.ReactNode;
  description: string;
}[] = [
  {
    value: undefined,
    label: "Vertical",
    icon: <Rows3 className="h-4 w-4" />,
    description: "Options empilées (défaut)",
  },
  {
    value: "inline",
    label: "Inline",
    icon: <MoreHorizontal className="h-4 w-4" />,
    description: "Options alignées, retour à la ligne auto",
  },
  {
    value: "grid-2",
    label: "Grille 2 col.",
    icon: <Grid2X2 className="h-4 w-4" />,
    description: "2 options par ligne",
  },
  {
    value: "grid-3",
    label: "Grille 3 col.",
    icon: <Grid3X3 className="h-4 w-4" />,
    description: "3 options par ligne",
  },
];

const resultsPositionOptions: { value: ResultsPosition; label: string; description: string }[] = [
  { value: "right", label: "À droite", description: "Résultats affichés à côté du formulaire" },
  { value: "bottom", label: "En bas", description: "Résultats affichés sous le formulaire" },
];

const resultsWidthOptions: { value: ResultsWidth; label: string; description: string }[] = [
  { value: "third", label: "1/3", description: "Résultats sur 1/3 de la largeur" },
  { value: "half", label: "1/2", description: "Résultats sur la moitié de la largeur" },
  { value: "full", label: "Pleine", description: "Résultats sur toute la largeur" },
];

const attachmentsPositionOptions: { value: "form" | "results" | "bottom"; label: string; description: string }[] = [
  { value: "form", label: "Dans le formulaire", description: "Section affichée à côté des champs de saisie" },
  { value: "results", label: "Dans les résultats", description: "Section affichée avec les résultats" },
  { value: "bottom", label: "En bas", description: "Section affichée en dessous de tout" },
];

// Preset colors for group background (La Poste palette)
const GROUP_PRESET_COLORS = [
  "#ffffff",  // blanc
  "#f8f9fa",  // gris très clair
  "#e9ecef",  // gris clair
  "#FFF5CC",  // laposte-yellow-light
  "#CCEFED",  // laposte-teal-light
  "#CCE0F0",  // laposte-blue-light
  "#FACCE6",  // laposte-magenta-light
  "#FFCC00",  // laposte-yellow
  "#00B2A9",  // laposte-teal
  "#003366",  // laposte-navy
];

// Available icons for groups
const availableIcons: { name: string; icon: LucideIcon; label: string }[] = [
  { name: "folder", icon: Folder, label: "Dossier" },
  { name: "file-text", icon: FileText, label: "Document" },
  { name: "user", icon: User, label: "Utilisateur" },
  { name: "package", icon: Package, label: "Colis" },
  { name: "settings", icon: Settings, label: "Paramètres" },
  { name: "briefcase", icon: Briefcase, label: "Affaires" },
  { name: "building", icon: Building2, label: "Bâtiment" },
  { name: "truck", icon: Truck, label: "Transport" },
  { name: "credit-card", icon: CreditCard, label: "Paiement" },
  { name: "calendar", icon: Calendar, label: "Calendrier" },
  { name: "map-pin", icon: MapPin, label: "Localisation" },
  { name: "tag", icon: Tag, label: "Étiquette" },
  { name: "bar-chart", icon: BarChart3, label: "Graphique" },
  { name: "pie-chart", icon: PieChart, label: "Camembert" },
  { name: "trending-up", icon: TrendingUp, label: "Tendance" },
  { name: "percent", icon: Percent, label: "Pourcentage" },
  { name: "dollar", icon: DollarSign, label: "Dollar" },
  { name: "euro", icon: Euro, label: "Euro" },
  { name: "hash", icon: Hash, label: "Nombre" },
  { name: "clock", icon: Clock, label: "Temps" },
  { name: "shield", icon: Shield, label: "Sécurité" },
  { name: "zap", icon: Zap, label: "Éclair" },
  { name: "target", icon: Target, label: "Cible" },
  { name: "award", icon: Award, label: "Récompense" },
  { name: "star", icon: Star, label: "Étoile" },
  { name: "heart", icon: Heart, label: "Coeur" },
  { name: "flag", icon: Flag, label: "Drapeau" },
  { name: "bookmark", icon: Bookmark, label: "Signet" },
  { name: "calculator", icon: Calculator, label: "Calculatrice" },
  { name: "layout-grid", icon: LayoutGrid, label: "Grille" },
];

// Helper to get icon component by name
function getIconByName(name: string | undefined): LucideIcon {
  if (!name) return Folder;
  const found = availableIcons.find((i) => i.name === name);
  return found?.icon || Folder;
}

// Flatten groups for display
function flattenFormGroups(groups: FormFieldGroupWithFields[], depth = 0): (FormFieldGroupWithFields & { depth: number })[] {
  const result: (FormFieldGroupWithFields & { depth: number })[] = [];
  for (const group of groups) {
    result.push({ ...group, depth });
    if (group.children.length > 0) {
      result.push(...flattenFormGroups(group.children, depth + 1));
    }
  }
  return result;
}

function flattenOutputGroups(groups: OutputFieldGroupWithFields[], depth = 0): (OutputFieldGroupWithFields & { depth: number })[] {
  const result: (OutputFieldGroupWithFields & { depth: number })[] = [];
  for (const group of groups) {
    result.push({ ...group, depth });
    if (group.children.length > 0) {
      result.push(...flattenOutputGroups(group.children, depth + 1));
    }
  }
  return result;
}

// Icon Picker Component
function IconPicker({
  value,
  onChange,
  disabled,
}: {
  value: string | undefined;
  onChange: (icon: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const IconComponent = getIconByName(value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          className="h-9 w-9 p-0"
        >
          <IconComponent className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" align="start">
        <div className="space-y-2">
          <Label className="text-sm font-medium">Choisir une icône</Label>
          <div className="grid grid-cols-6 gap-1">
            {availableIcons.map((iconOption) => {
              const Icon = iconOption.icon;
              return (
                <button
                  key={iconOption.name}
                  onClick={() => {
                    onChange(iconOption.name);
                    setOpen(false);
                  }}
                  className={`p-2 rounded-md hover:bg-muted transition-colors ${
                    value === iconOption.name ? "bg-primary/10 ring-1 ring-primary" : ""
                  }`}
                  title={iconOption.label}
                >
                  <Icon className="h-4 w-4" />
                </button>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ============== Simulator Preview Components ==============

type ViewMode = "desktop" | "mobile";

// Helper function to get card style class
function getCardStyleClass(cardStyle: string | undefined): string {
  switch (cardStyle) {
    case "bordered":
      return "border-2 border-border rounded-lg";
    case "elevated":
      return "shadow-lg rounded-lg bg-card border border-border/50";
    case "minimal":
      return "";
    default:
      return "bg-muted/30 rounded-lg border border-transparent";
  }
}

// Helper function to get layout class for field container
function getLayoutClass(layout: string | undefined): string {
  switch (layout) {
    case "horizontal":
      return "grid grid-cols-12 gap-3";
    case "grid-2":
      return "grid grid-cols-12 gap-3";
    case "grid-3":
      return "grid grid-cols-12 gap-3";
    default:
      return "grid grid-cols-12 gap-3";
  }
}

// Helper function to get children grid class based on children_per_row
function getChildrenGridClass(childrenPerRow: number | undefined, isMobile: boolean): string {
  if (isMobile) return "grid-cols-1";
  switch (childrenPerRow) {
    case 2:
      return "grid-cols-1 lg:grid-cols-2";
    case 3:
      return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";
    default:
      return "grid-cols-1";
  }
}

// Helper function to get field width class based on individual field width or group layout
function getFieldWidthClass(fieldWidth: string | null | undefined, groupLayout: string | undefined, isMobile: boolean): string {
  if (isMobile) return "col-span-12";
  
  // If field has explicit width, use it
  if (fieldWidth) {
    switch (fieldWidth) {
      case "half":
        return "col-span-12 sm:col-span-6";
      case "third":
        return "col-span-12 sm:col-span-4";
      case "quarter":
        return "col-span-12 sm:col-span-3";
      default:
        return "col-span-12";
    }
  }
  
  // Otherwise, use the group layout default
  switch (groupLayout) {
    case "horizontal":
      return "col-span-12 sm:col-span-4";
    case "grid-2":
      return "col-span-12 sm:col-span-6";
    case "grid-3":
      return "col-span-12 sm:col-span-4";
    case "grid-4":
      return "col-span-12 sm:col-span-3";
    case "grid-5":
      return "col-span-12 sm:col-span-2";
    default:
      return "col-span-12";
  }
}

// Mock field renderer for preview
function PreviewFormField({ 
  field, 
  widthClass,
  showSpinbox = true,
}: { 
  field: FormField; 
  widthClass: string;
  showSpinbox?: boolean;
}) {
  const renderFieldInput = () => {
    switch (field.field_type) {
      case "checkbox":
        return (
          <div className="flex items-center gap-2">
            <Checkbox disabled checked={!!field.default_value} />
            <span className="text-sm">{field.label}</span>
          </div>
        );
      case "select":
      case "multiselect":
        return (
          <Select disabled>
            <SelectTrigger className="bg-background">
              <SelectValue placeholder={field.placeholder || "Sélectionner..."} />
            </SelectTrigger>
          </Select>
        );
      case "radio":
        const radioOptions = field.enum_values?.slice(0, 3) || ["Option 1", "Option 2"];
        return (
          <div className="flex flex-col gap-2">
            {radioOptions.map((option, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div className="h-4 w-4 rounded-full border border-primary flex items-center justify-center">
                  {idx === 0 && <div className="h-2 w-2 rounded-full bg-primary" />}
                </div>
                <span className="text-sm">{option}</span>
              </div>
            ))}
          </div>
        );
      case "textarea":
        return (
          <div className="h-20 rounded-md border bg-background px-3 py-2 text-sm text-muted-foreground">
            {field.placeholder || "..."}
          </div>
        );
      case "number":
        return (
          <Input 
            type="number" 
            disabled 
            placeholder={field.placeholder || "0"} 
            className={`bg-background ${!showSpinbox ? "no-spinbox" : ""}`}
          />
        );
      case "date":
        return (
          <Input 
            type="text" 
            disabled 
            placeholder={field.placeholder || "JJ/MM/AAAA"} 
            className="bg-background"
          />
        );
      default:
        return (
          <Input 
            disabled 
            placeholder={field.placeholder || "..."} 
            className="bg-background"
          />
        );
    }
  };

  if (field.field_type === "checkbox") {
    return (
      <div className={widthClass}>
        <div className="flex items-center gap-2">
          <Checkbox disabled checked={!!field.default_value} />
          <span className="text-sm">{field.label}</span>
          {field.tooltip && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-muted-foreground cursor-help text-xs">ⓘ</span>
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">{field.tooltip}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-1.5 ${widthClass}`}>
      <div className="flex items-center gap-1.5">
        <Label className="text-sm font-medium">
          {field.label}
          {field.required && <span className="text-destructive ml-0.5">*</span>}
        </Label>
        {field.tooltip && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-muted-foreground cursor-help text-xs">ⓘ</span>
            </TooltipTrigger>
            <TooltipContent>
              <p className="max-w-xs">{field.tooltip}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      {renderFieldInput()}
      {field.description && (
        <p className="text-xs text-muted-foreground">{field.description}</p>
      )}
    </div>
  );
}

// Preview group renderer for form fields
function PreviewFormGroup({ 
  group, 
  depth = 0,
  isMobile,
  showSpinbox = true,
}: { 
  group: FormFieldGroupWithFields; 
  depth?: number;
  isMobile: boolean;
  showSpinbox?: boolean;
}) {
  const layoutOptions = group.layout_options || {};
  const layout = layoutOptions.layout || "vertical";
  const cardStyle = layoutOptions.card_style || "default";
  const collapsedByDefault = layoutOptions.collapsed_by_default ?? false;
  const showHeader = layoutOptions.show_header ?? true;
  const childrenPerRow = layoutOptions.children_per_row ?? 1;
  const icon = layoutOptions.icon;
  const IconComponent = getIconByName(icon);
  
  const [isOpen, setIsOpen] = useState(!collapsedByDefault);
  
  const cardClass = getCardStyleClass(cardStyle);
  const layoutClass = getLayoutClass(layout);
  const childrenGridClass = getChildrenGridClass(childrenPerRow, isMobile);
  
  const visibleFields = group.fields
    .filter((field) => field.visible !== false)
    .sort((a, b) => a.order - b.order);
  
  if (visibleFields.length === 0 && group.children.length === 0) return null;
  
  const content = (
    <div className="space-y-4">
      {visibleFields.length > 0 && (
        <div className={layoutClass}>
          {visibleFields.map((field) => (
            <PreviewFormField
              key={field.id}
              field={field}
              widthClass={getFieldWidthClass(field.width, layout, isMobile)}
              showSpinbox={showSpinbox}
            />
          ))}
        </div>
      )}
      
      {group.children.length > 0 && (
        <div className={`grid ${childrenGridClass} gap-4 ${visibleFields.length > 0 ? "mt-4" : ""}`}>
          {group.children.sort((a, b) => a.order - b.order).map((child) => (
            <PreviewFormGroup
              key={child.id}
              group={child}
              depth={depth + 1}
              isMobile={isMobile}
              showSpinbox={showSpinbox}
            />
          ))}
        </div>
      )}
    </div>
  );
  
  if (!showHeader) {
    return (
      <div className={`${cardClass} ${cardStyle !== "minimal" ? "p-4" : ""}`}>
        {content}
      </div>
    );
  }
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className={`${cardClass} ${cardStyle !== "minimal" ? "p-4" : ""}`}>
        <CollapsibleTrigger className="flex items-center gap-2 w-full text-left mb-3 hover:opacity-80 transition-opacity">
          {isOpen ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
          <IconComponent className="h-4 w-4 text-muted-foreground" />
          <div className="flex-1">
            <h4 className="font-semibold text-sm">{group.name}</h4>
            {group.description && (
              <p className="text-xs text-muted-foreground">{group.description}</p>
            )}
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
              >
                {content}
              </motion.div>
            )}
          </AnimatePresence>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

// Preview output field renderer
function PreviewOutputField({ 
  field, 
  widthClass,
}: { 
  field: OutputField; 
  widthClass: string;
}) {
  // Generate mock values based on output type
  const getMockValue = () => {
    switch (field.output_type) {
      case "currency":
        return "1 234,56 €";
      case "percentage":
        return "42,5 %";
      case "boolean":
        return "Oui";
      case "number":
        return "1 234";
      case "date":
        return "12/12/2025";
      default:
        return "Résultat";
    }
  };
  
  return (
    <div className={`${widthClass}`}>
      <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground truncate">{field.label}</p>
          <p className="font-semibold text-lg tabular-nums">{getMockValue()}</p>
        </div>
      </div>
    </div>
  );
}

// Preview group renderer for output fields
function PreviewOutputGroup({ 
  group, 
  depth = 0,
  isMobile,
}: { 
  group: OutputFieldGroupWithFields; 
  depth?: number;
  isMobile: boolean;
}) {
  const layoutOptions = group.layout_options || {};
  const layout = layoutOptions.layout || "vertical";
  const cardStyle = layoutOptions.card_style || "default";
  const collapsedByDefault = layoutOptions.collapsed_by_default ?? false;
  const showHeader = layoutOptions.show_header ?? true;
  const childrenPerRow = layoutOptions.children_per_row ?? 1;
  const icon = layoutOptions.icon;
  const IconComponent = getIconByName(icon);
  
  const [isOpen, setIsOpen] = useState(!collapsedByDefault);
  
  const cardClass = getCardStyleClass(cardStyle);
  const layoutClass = getLayoutClass(layout);
  const childrenGridClass = getChildrenGridClass(childrenPerRow, isMobile);
  
  const visibleFields = group.fields.filter(f => f.visibility_mode !== 'never').sort((a, b) => a.order - b.order);
  
  if (visibleFields.length === 0 && group.children.length === 0) return null;
  
  const content = (
    <div className="space-y-4">
      {visibleFields.length > 0 && (
        <div className={layoutClass}>
          {visibleFields.map((field) => (
            <PreviewOutputField
              key={field.id}
              field={field}
              widthClass={getFieldWidthClass(null, layout, isMobile)}
            />
          ))}
        </div>
      )}
      
      {group.children.length > 0 && (
        <div className={`grid ${childrenGridClass} gap-4 ${visibleFields.length > 0 ? "mt-4" : ""}`}>
          {group.children.sort((a, b) => a.order - b.order).map((child) => (
            <PreviewOutputGroup
              key={child.id}
              group={child}
              depth={depth + 1}
              isMobile={isMobile}
            />
          ))}
        </div>
      )}
    </div>
  );
  
  if (!showHeader) {
    return (
      <div className={`${cardClass} ${cardStyle !== "minimal" ? "p-4" : ""}`}>
        {content}
      </div>
    );
  }
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className={`${cardClass} ${cardStyle !== "minimal" ? "p-4" : ""}`}>
        <CollapsibleTrigger className="flex items-center gap-2 w-full text-left mb-3 hover:opacity-80 transition-opacity">
          {isOpen ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
          <IconComponent className="h-4 w-4 text-muted-foreground" />
          <div className="flex-1">
            <h4 className="font-semibold text-sm">{group.name}</h4>
            {group.description && (
              <p className="text-xs text-muted-foreground">{group.description}</p>
            )}
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
              >
                {content}
              </motion.div>
            )}
          </AnimatePresence>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

// Main simulator preview component
interface SimulatorPreviewProps {
  formFieldGroups: FormFieldGroupWithFields[];
  outputFieldGroups: OutputFieldGroupWithFields[];
  appearanceOptions?: AppearanceOptions | null;
}

function SimulatorPreview({ formFieldGroups, outputFieldGroups, appearanceOptions }: SimulatorPreviewProps) {
  const [viewMode, setViewMode] = useState<ViewMode>("desktop");
  const [isExpanded, setIsExpanded] = useState(false);
  const [showResults, setShowResults] = useState(true);
  
  // Get appearance options with defaults
  const formGroupsPerRow = appearanceOptions?.form_groups_per_row ?? 1;
  const outputGroupsPerRow = appearanceOptions?.output_groups_per_row ?? 1;
  const resultsPosition = appearanceOptions?.results_position ?? "right";
  const resultsWidth = appearanceOptions?.results_width ?? "half";
  const showSpinbox = appearanceOptions?.show_spinbox ?? true;
  
  const sortedFormGroups = useMemo(() => 
    [...formFieldGroups].sort((a, b) => a.order - b.order),
    [formFieldGroups]
  );
  
  const sortedOutputGroups = useMemo(() => 
    [...outputFieldGroups].sort((a, b) => a.order - b.order),
    [outputFieldGroups]
  );
  
  const containerClass = viewMode === "mobile" 
    ? "max-w-[375px] mx-auto" 
    : "w-full";
  
  const resetPreview = () => {
    setShowResults(false);
    setTimeout(() => setShowResults(true), 100);
  };
  
  // Helper to get form groups grid class
  const getFormGroupsGridClass = () => {
    if (viewMode === "mobile") return "grid-cols-1";
    switch (formGroupsPerRow) {
      case 2: return "grid-cols-1 lg:grid-cols-2";
      case 3: return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";
      default: return "grid-cols-1";
    }
  };
  
  // Helper to get individual form group wrapper class (for full_width support)
  const getFormGroupWrapperClass = (group: FormFieldGroupWithFields) => {
    if (viewMode === "mobile") return "";
    const isFullWidth = group.layout_options?.full_width === true;
    if (formGroupsPerRow === 1) return ""; // No special class needed for single column
    if (isFullWidth) {
      switch (formGroupsPerRow) {
        case 2: return "lg:col-span-2";
        case 3: return "md:col-span-2 lg:col-span-3";
        default: return "";
      }
    }
    return "";
  };
  
  // Helper to get output groups grid class
  const getOutputGroupsGridClass = () => {
    if (viewMode === "mobile") return "grid-cols-1";
    switch (outputGroupsPerRow) {
      case 2: return "grid-cols-1 lg:grid-cols-2";
      case 3: return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";
      default: return "grid-cols-1";
    }
  };
  
  // Helper to get individual output group wrapper class (for full_width support)
  const getOutputGroupWrapperClass = (group: OutputFieldGroupWithFields) => {
    if (viewMode === "mobile") return "";
    const isFullWidth = group.layout_options?.full_width === true;
    if (outputGroupsPerRow === 1) return ""; // No special class needed for single column
    if (isFullWidth) {
      switch (outputGroupsPerRow) {
        case 2: return "lg:col-span-2";
        case 3: return "md:col-span-2 lg:col-span-3";
        default: return "";
      }
    }
    return "";
  };
  
  // Helper to get results width class
  const getResultsWidthClass = () => {
    if (viewMode === "mobile" || resultsPosition === "bottom") return "col-span-12";
    switch (resultsWidth) {
      case "third": return "col-span-12 lg:col-span-4";
      case "full": return "col-span-12";
      default: return "col-span-12 lg:col-span-6";
    }
  };
  
  // Helper to get form width class
  const getFormWidthClass = () => {
    if (viewMode === "mobile" || resultsPosition === "bottom") return "col-span-12";
    switch (resultsWidth) {
      case "third": return "col-span-12 lg:col-span-8";
      case "full": return "col-span-12";
      default: return "col-span-12 lg:col-span-6";
    }
  };
  
  return (
    <Card className={isExpanded ? "fixed inset-4 z-50 flex flex-col overflow-hidden" : ""}>
      <CardHeader className="pb-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Aperçu du simulateur
            </CardTitle>
            <CardDescription>
              Prévisualisation en temps réel avec les styles appliqués
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {/* View mode toggle */}
            <div className="flex items-center gap-1 p-1 bg-muted rounded-lg">
              <Button
                variant={viewMode === "desktop" ? "secondary" : "ghost"}
                size="sm"
                className="h-7 px-2"
                onClick={() => setViewMode("desktop")}
              >
                <Monitor className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "mobile" ? "secondary" : "ghost"}
                size="sm"
                className="h-7 px-2"
                onClick={() => setViewMode("mobile")}
              >
                <Smartphone className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Reset button */}
            <Button
              variant="outline"
              size="sm"
              className="h-7"
              onClick={resetPreview}
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </Button>
            
            {/* Expand/Collapse */}
            <Button
              variant="outline"
              size="sm"
              className="h-7"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? (
                <Minimize2 className="h-3.5 w-3.5" />
              ) : (
                <Maximize2 className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className={`flex-1 overflow-auto ${isExpanded ? "pb-6" : ""}`}>
        {/* Preview container with device frame */}
        <div className={`
          ${viewMode === "mobile" ? "bg-muted/30 rounded-xl p-4" : ""}
          transition-all duration-300
        `}>
          <div className={containerClass}>
            {/* Device frame for mobile */}
            {viewMode === "mobile" && (
              <div className="bg-card rounded-[2rem] border-4 border-muted-foreground/20 shadow-xl overflow-hidden">
                {/* Notch */}
                <div className="h-6 bg-muted-foreground/10 flex items-center justify-center">
                  <div className="w-20 h-1.5 bg-muted-foreground/30 rounded-full" />
                </div>
                
                <div className="p-4 space-y-6 max-h-[600px] overflow-auto">
                  {/* Form section */}
                  {sortedFormGroups.length > 0 && (
                    <div className="space-y-4">
                      <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                        Formulaire
                      </h3>
                      {sortedFormGroups.map((group) => (
                        <PreviewFormGroup
                          key={group.id}
                          group={group}
                          isMobile={true}
                          showSpinbox={showSpinbox}
                        />
                      ))}
                      
                      {/* Simulate button */}
                      <Button className="w-full" disabled>
                        <Play className="mr-2 h-4 w-4" />
                        Simuler
                      </Button>
                    </div>
                  )}
                  
                  {/* Results section */}
                  {showResults && sortedOutputGroups.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="space-y-4"
                    >
                      <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                        Résultats
                      </h3>
                      {sortedOutputGroups.map((group) => (
                        <PreviewOutputGroup
                          key={group.id}
                          group={group}
                          isMobile={true}
                        />
                      ))}
                    </motion.div>
                  )}
                </div>
                
                {/* Home indicator */}
                <div className="h-8 flex items-center justify-center">
                  <div className="w-32 h-1 bg-muted-foreground/30 rounded-full" />
                </div>
              </div>
            )}
            
            {/* Desktop view */}
            {viewMode === "desktop" && (
              <div className="bg-card rounded-lg border shadow-sm overflow-hidden">
                {/* Browser-like header */}
                <div className="h-10 bg-muted/50 border-b flex items-center px-4 gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-error" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-success" />
                  </div>
                  <div className="flex-1 ml-4">
                    <div className="h-5 bg-muted rounded-md flex items-center px-3">
                      <span className="text-xs text-muted-foreground">simulateur.laposte.fr</span>
                    </div>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className={`grid grid-cols-12 gap-6 ${resultsPosition === "bottom" ? "" : ""}`}>
                    {/* Form section */}
                    {sortedFormGroups.length > 0 && (
                      <div className={`space-y-4 ${getFormWidthClass()}`}>
                        <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">1</span>
                          Formulaire
                        </h3>
                        <div className={`grid ${getFormGroupsGridClass()} gap-4`}>
                          {sortedFormGroups.map((group) => (
                            <div key={group.id} className={getFormGroupWrapperClass(group)}>
                              <PreviewFormGroup
                                group={group}
                                isMobile={false}
                                showSpinbox={showSpinbox}
                              />
                            </div>
                          ))}
                        </div>
                        
                        {/* Simulate button */}
                        <Button className="w-full" disabled>
                          <Play className="mr-2 h-4 w-4" />
                          Lancer la simulation
                        </Button>
                      </div>
                    )}
                    
                    {/* Results section */}
                    {showResults && sortedOutputGroups.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, x: resultsPosition === "right" ? 10 : 0, y: resultsPosition === "bottom" ? 10 : 0 }}
                        animate={{ opacity: 1, x: 0, y: 0 }}
                        className={`space-y-4 ${getResultsWidthClass()}`}
                      >
                        <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-success/10 text-success flex items-center justify-center text-xs font-bold">2</span>
                          Résultats
                        </h3>
                        <div className={`grid ${getOutputGroupsGridClass()} gap-4`}>
                          {sortedOutputGroups.map((group) => (
                            <div key={group.id} className={getOutputGroupWrapperClass(group)}>
                              <PreviewOutputGroup
                                group={group}
                                isMobile={false}
                              />
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Empty state */}
        {sortedFormGroups.length === 0 && sortedOutputGroups.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <Eye className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Ajoutez des groupes de champs pour voir l&apos;aperçu</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function AppearanceTab({ versionId, simulatorId, isEditable }: AppearanceTabProps) {
  const queryClient = useQueryClient();
  
  // State for switching between config and preview modes
  const [activeView, setActiveView] = useState<"config" | "preview">("config");

  // Fetch version data for appearance options
  const { data: versionData, isLoading: loadingVersion } = useQuery({
    queryKey: ["version", versionId],
    queryFn: () => getVersion(versionId),
    enabled: !!versionId,
  });

  // Fetch form field groups
  const { data: formFieldGroups, isLoading: loadingFormGroups } = useQuery({
    queryKey: ["form-field-groups", versionId],
    queryFn: () => listAllFormFieldGroups(versionId),
    enabled: !!versionId,
  });

  // Fetch output field groups
  const { data: outputFieldGroups, isLoading: loadingOutputGroups } = useQuery({
    queryKey: ["output-field-groups", versionId],
    queryFn: () => listAllOutputFieldGroups(versionId),
    enabled: !!versionId,
  });

  // Update version appearance options mutation
  const updateAppearanceMutation = useMutation({
    mutationFn: (newOptions: AppearanceOptions) =>
      updateVersion(versionId, { appearance_options: newOptions }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["version", versionId] });
      queryClient.invalidateQueries({ queryKey: ["simulator", simulatorId] });
      toast.success("Options d'apparence mises à jour");
    },
    onError: (error) => {
      toast.error("Erreur", { description: getErrorMessage(error) });
    },
  });

  // Current appearance options with defaults
  const appearanceOptions: AppearanceOptions = versionData?.appearance_options || {};
  const currentFormGroupsPerRow = appearanceOptions.form_groups_per_row ?? 1;
  const currentOutputGroupsPerRow = appearanceOptions.output_groups_per_row ?? 1;
  const currentResultsPosition = appearanceOptions.results_position ?? "right";
  const currentResultsWidth = appearanceOptions.results_width ?? "half";
  const currentAttachmentsPosition = appearanceOptions.attachments_position ?? "bottom";
  const currentShowOutputDescriptions = appearanceOptions.show_output_descriptions ?? true;
  const currentShowSpinbox = appearanceOptions.show_spinbox ?? true;
  
  // Get settings to check if attachments are enabled
  const attachmentsEnabled = versionData?.settings?.enable_attachments ?? false;

  // Handlers for global appearance options
  const handleFormGroupsPerRowChange = (value: FormGroupsPerRow) => {
    updateAppearanceMutation.mutate({ ...appearanceOptions, form_groups_per_row: value });
  };

  const handleOutputGroupsPerRowChange = (value: OutputGroupsPerRow) => {
    updateAppearanceMutation.mutate({ ...appearanceOptions, output_groups_per_row: value });
  };

  const handleResultsPositionChange = (value: ResultsPosition) => {
    updateAppearanceMutation.mutate({ ...appearanceOptions, results_position: value });
  };

  const handleResultsWidthChange = (value: ResultsWidth) => {
    updateAppearanceMutation.mutate({ ...appearanceOptions, results_width: value });
  };
  
  const handleAttachmentsPositionChange = (value: AttachmentsPosition) => {
    updateAppearanceMutation.mutate({ ...appearanceOptions, attachments_position: value });
  };

  const handleShowOutputDescriptionsChange = (value: boolean) => {
    updateAppearanceMutation.mutate({ ...appearanceOptions, show_output_descriptions: value });
  };

  const handleShowSpinboxChange = (value: boolean) => {
    updateAppearanceMutation.mutate({ ...appearanceOptions, show_spinbox: value });
  };

  // Flatten groups for easier iteration
  const flattenedFormGroups = useMemo(() => {
    if (!formFieldGroups) return [];
    return flattenFormGroups(formFieldGroups);
  }, [formFieldGroups]);

  const flattenedOutputGroups = useMemo(() => {
    if (!outputFieldGroups) return [];
    return flattenOutputGroups(outputFieldGroups);
  }, [outputFieldGroups]);

  // Update form group mutation
  const updateFormGroupMutation = useMutation({
    mutationFn: ({ groupId, data }: { groupId: number; data: { layout_options: GroupLayoutOptions } }) =>
      updateFormFieldGroup(versionId, groupId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["form-field-groups", versionId] });
      toast.success("Apparence mise à jour");
    },
    onError: (error) => {
      toast.error("Erreur", { description: getErrorMessage(error) });
    },
  });

  // Update output group mutation
  const updateOutputGroupMutation = useMutation({
    mutationFn: ({ groupId, data }: { groupId: number; data: { layout_options: GroupLayoutOptions } }) =>
      updateOutputFieldGroup(versionId, groupId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["output-field-groups", versionId] });
      toast.success("Apparence mise à jour");
    },
    onError: (error) => {
      toast.error("Erreur", { description: getErrorMessage(error) });
    },
  });

  // Handlers for form groups
  const handleFormLayoutChange = (groupId: number, currentOptions: GroupLayoutOptions | null, layout: GroupLayout) => {
    const newOptions: GroupLayoutOptions = { ...currentOptions, layout };
    updateFormGroupMutation.mutate({ groupId, data: { layout_options: newOptions } });
  };

  const handleFormCardStyleChange = (groupId: number, currentOptions: GroupLayoutOptions | null, cardStyle: GroupCardStyle) => {
    const newOptions: GroupLayoutOptions = { ...currentOptions, card_style: cardStyle };
    updateFormGroupMutation.mutate({ groupId, data: { layout_options: newOptions } });
  };

  const handleFormCollapsedChange = (groupId: number, currentOptions: GroupLayoutOptions | null, collapsed: boolean) => {
    const newOptions: GroupLayoutOptions = { ...currentOptions, collapsed_by_default: collapsed };
    updateFormGroupMutation.mutate({ groupId, data: { layout_options: newOptions } });
  };

  const handleFormShowHeaderChange = (groupId: number, currentOptions: GroupLayoutOptions | null, showHeader: boolean) => {
    const newOptions: GroupLayoutOptions = { ...currentOptions, show_header: showHeader };
    updateFormGroupMutation.mutate({ groupId, data: { layout_options: newOptions } });
  };

  const handleFormIconChange = (groupId: number, currentOptions: GroupLayoutOptions | null, icon: string) => {
    const newOptions: GroupLayoutOptions = { ...currentOptions, icon };
    updateFormGroupMutation.mutate({ groupId, data: { layout_options: newOptions } });
  };

  const handleFormFullWidthChange = (groupId: number, currentOptions: GroupLayoutOptions | null, fullWidth: boolean) => {
    const newOptions: GroupLayoutOptions = { ...currentOptions, full_width: fullWidth };
    updateFormGroupMutation.mutate({ groupId, data: { layout_options: newOptions } });
  };

  const handleFormChildrenPerRowChange = (groupId: number, currentOptions: GroupLayoutOptions | null, childrenPerRow: ChildrenPerRow) => {
    const newOptions: GroupLayoutOptions = { ...currentOptions, children_per_row: childrenPerRow };
    updateFormGroupMutation.mutate({ groupId, data: { layout_options: newOptions } });
  };

  const handleFormBackgroundColorChange = (groupId: number, currentOptions: GroupLayoutOptions | null, backgroundColor: string | undefined) => {
    const newOptions: GroupLayoutOptions = { ...currentOptions, background_color: backgroundColor };
    updateFormGroupMutation.mutate({ groupId, data: { layout_options: newOptions } });
  };

  const handleFormRadioLayoutChange = (groupId: number, currentOptions: GroupLayoutOptions | null, radioLayout: RadioLayout | undefined) => {
    const newOptions: GroupLayoutOptions = { ...currentOptions };
    if (radioLayout === undefined) {
      delete newOptions.radio_layout;
    } else {
      newOptions.radio_layout = radioLayout;
    }
    updateFormGroupMutation.mutate({ groupId, data: { layout_options: newOptions } });
  };

  // Handlers for output groups
  const handleOutputLayoutChange = (groupId: number, currentOptions: GroupLayoutOptions | null, layout: GroupLayout) => {
    const newOptions: GroupLayoutOptions = { ...currentOptions, layout };
    updateOutputGroupMutation.mutate({ groupId, data: { layout_options: newOptions } });
  };

  const handleOutputCardStyleChange = (groupId: number, currentOptions: GroupLayoutOptions | null, cardStyle: GroupCardStyle) => {
    const newOptions: GroupLayoutOptions = { ...currentOptions, card_style: cardStyle };
    updateOutputGroupMutation.mutate({ groupId, data: { layout_options: newOptions } });
  };

  const handleOutputCollapsedChange = (groupId: number, currentOptions: GroupLayoutOptions | null, collapsed: boolean) => {
    const newOptions: GroupLayoutOptions = { ...currentOptions, collapsed_by_default: collapsed };
    updateOutputGroupMutation.mutate({ groupId, data: { layout_options: newOptions } });
  };

  const handleOutputShowHeaderChange = (groupId: number, currentOptions: GroupLayoutOptions | null, showHeader: boolean) => {
    const newOptions: GroupLayoutOptions = { ...currentOptions, show_header: showHeader };
    updateOutputGroupMutation.mutate({ groupId, data: { layout_options: newOptions } });
  };

  const handleOutputIconChange = (groupId: number, currentOptions: GroupLayoutOptions | null, icon: string) => {
    const newOptions: GroupLayoutOptions = { ...currentOptions, icon };
    updateOutputGroupMutation.mutate({ groupId, data: { layout_options: newOptions } });
  };

  const handleOutputFullWidthChange = (groupId: number, currentOptions: GroupLayoutOptions | null, fullWidth: boolean) => {
    const newOptions: GroupLayoutOptions = { ...currentOptions, full_width: fullWidth };
    updateOutputGroupMutation.mutate({ groupId, data: { layout_options: newOptions } });
  };

  const handleOutputChildrenPerRowChange = (groupId: number, currentOptions: GroupLayoutOptions | null, childrenPerRow: ChildrenPerRow) => {
    const newOptions: GroupLayoutOptions = { ...currentOptions, children_per_row: childrenPerRow };
    updateOutputGroupMutation.mutate({ groupId, data: { layout_options: newOptions } });
  };

  const handleOutputBackgroundColorChange = (groupId: number, currentOptions: GroupLayoutOptions | null, backgroundColor: string | undefined) => {
    const newOptions: GroupLayoutOptions = { ...currentOptions, background_color: backgroundColor };
    updateOutputGroupMutation.mutate({ groupId, data: { layout_options: newOptions } });
  };

  const handleOutputHideParentNameChange = (groupId: number, currentOptions: GroupLayoutOptions | null, hideParentName: boolean) => {
    const newOptions: GroupLayoutOptions = { ...currentOptions, hide_parent_name: hideParentName };
    updateOutputGroupMutation.mutate({ groupId, data: { layout_options: newOptions } });
  };

  if (loadingFormGroups || loadingOutputGroups || loadingVersion) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  const hasNoGroups = !flattenedFormGroups.length && !flattenedOutputGroups.length;

  if (hasNoGroups) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Palette className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold mb-2">Aucun groupe de champs</h3>
          <p className="text-muted-foreground">
            Créez d&apos;abord des groupes de champs dans les onglets &quot;Formulaire&quot; ou &quot;Calculs&quot; pour personnaliser leur apparence.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Render group configuration accordion item
  const renderGroupConfig = (
    group: (FormFieldGroupWithFields | OutputFieldGroupWithFields) & { depth: number },
    type: "form" | "output",
    handlers: {
      onLayoutChange: (id: number, opts: GroupLayoutOptions | null, layout: GroupLayout) => void;
      onCardStyleChange: (id: number, opts: GroupLayoutOptions | null, style: GroupCardStyle) => void;
      onCollapsedChange: (id: number, opts: GroupLayoutOptions | null, collapsed: boolean) => void;
      onShowHeaderChange: (id: number, opts: GroupLayoutOptions | null, show: boolean) => void;
      onIconChange: (id: number, opts: GroupLayoutOptions | null, icon: string) => void;
      onFullWidthChange: (id: number, opts: GroupLayoutOptions | null, fullWidth: boolean) => void;
      onChildrenPerRowChange: (id: number, opts: GroupLayoutOptions | null, childrenPerRow: ChildrenPerRow) => void;
      onBackgroundColorChange: (id: number, opts: GroupLayoutOptions | null, color: string | undefined) => void;
      onHideParentNameChange?: (id: number, opts: GroupLayoutOptions | null, hideParentName: boolean) => void;
      onRadioLayoutChange?: (id: number, opts: GroupLayoutOptions | null, radioLayout: RadioLayout | undefined) => void;
    },
    isPending: boolean
  ) => {
    const layoutOption = group.layout_options || {};
    const currentLayout = layoutOption.layout || "vertical";
    const currentCardStyle = layoutOption.card_style || "default";
    const collapsedByDefault = layoutOption.collapsed_by_default ?? false;
    const showHeader = layoutOption.show_header ?? true;
    const currentIcon = layoutOption.icon;
    const fullWidth = layoutOption.full_width ?? false;
    const currentChildrenPerRow = layoutOption.children_per_row ?? 1;
    const hideParentName = layoutOption.hide_parent_name ?? false;
    const IconComponent = getIconByName(currentIcon);
    const fields = group.fields;
    const hasChildren = group.children.length > 0;
    const isChildGroup = group.depth > 0;
    
    // Determine if full width option should be shown (only when groups_per_row > 1)
    const showFullWidthOption = type === "form" 
      ? currentFormGroupsPerRow > 1 
      : currentOutputGroupsPerRow > 1;

    return (
      <AccordionItem 
        key={`${type}-${group.id}`} 
        value={`${type}-group-${group.id}`}
        className="border rounded-lg px-4"
      >
        <AccordionTrigger className="py-3 hover:no-underline">
          <div className="flex items-center gap-3 flex-1">
            {/* Depth indicator */}
            {group.depth > 0 && (
              <div 
                className="h-4 border-l-2 border-muted-foreground/30"
                style={{ marginLeft: `${(group.depth - 1) * 16}px` }}
              />
            )}
            
            <div className="flex items-center gap-2 flex-1">
              <IconComponent className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{group.name}</span>
              <Badge variant="outline" className="text-xs">
                {fields.length} {type === "form" ? "champ" : "calcul"}{fields.length > 1 ? "s" : ""}
              </Badge>
              {fullWidth && showFullWidthOption && (
                <Badge variant="secondary" className="text-xs gap-1">
                  <Maximize2 className="h-3 w-3" />
                  Pleine largeur
                </Badge>
              )}
            </div>

            {/* Current layout preview */}
            <div className="flex items-center gap-2 mr-4">
              {layoutOptions.find((l) => l.value === currentLayout)?.icon}
              <span className="text-xs text-muted-foreground hidden sm:inline">
                {layoutOptions.find((l) => l.value === currentLayout)?.label}
              </span>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <div className="space-y-6 pt-4 pb-2">
            {/* Icon Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Icône du groupe</Label>
              <div className="flex items-center gap-3">
                <IconPicker
                  value={currentIcon}
                  onChange={(icon) => handlers.onIconChange(group.id, group.layout_options, icon)}
                  disabled={!isEditable || isPending}
                />
                <span className="text-sm text-muted-foreground">
                  {currentIcon ? availableIcons.find((i) => i.name === currentIcon)?.label || currentIcon : "Dossier (par défaut)"}
                </span>
              </div>
            </div>

            {/* Background Color Selection */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Couleur de fond</Label>
                <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">Mode clair uniquement</span>
              </div>
              <div className="flex items-center gap-3">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="gap-2"
                      disabled={!isEditable || isPending}
                    >
                      <div 
                        className="w-5 h-5 rounded border border-border"
                        style={{ backgroundColor: layoutOption.background_color || "transparent" }}
                      />
                      <Palette className="h-4 w-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64">
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium">Couleur de fond</label>
                        <p className="text-xs text-muted-foreground mt-0.5">Visible uniquement en mode clair</p>
                      </div>
                      <div className="grid grid-cols-5 gap-2">
                        {GROUP_PRESET_COLORS.map((color) => (
                          <button
                            key={color}
                            className={`w-8 h-8 rounded border hover:scale-110 transition-transform ${
                              layoutOption.background_color === color ? "ring-2 ring-primary ring-offset-2" : "border-border"
                            }`}
                            style={{ backgroundColor: color }}
                            onClick={() => handlers.onBackgroundColorChange(group.id, group.layout_options, color)}
                            title={color}
                          />
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={layoutOption.background_color || "#ffffff"}
                          onChange={(e) => handlers.onBackgroundColorChange(group.id, group.layout_options, e.target.value)}
                          className="w-12 h-8 p-0 border-0 cursor-pointer"
                        />
                        <Input
                          value={layoutOption.background_color || ""}
                          onChange={(e) => handlers.onBackgroundColorChange(group.id, group.layout_options, e.target.value)}
                          placeholder="#ffffff"
                          className="flex-1"
                        />
                      </div>
                      {layoutOption.background_color && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="w-full text-muted-foreground"
                          onClick={() => handlers.onBackgroundColorChange(group.id, group.layout_options, undefined)}
                        >
                          Supprimer la couleur
                        </Button>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
                <span className="text-sm text-muted-foreground">
                  {layoutOption.background_color || "Aucune (par défaut)"}
                </span>
              </div>
            </div>

            {/* Layout Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Disposition des {type === "form" ? "champs" : "calculs"}</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {layoutOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => isEditable && handlers.onLayoutChange(group.id, group.layout_options, option.value)}
                    disabled={!isEditable || isPending}
                    className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                      currentLayout === option.value
                        ? "border-primary bg-primary/5"
                        : "border-muted hover:border-muted-foreground/50"
                    } ${!isEditable ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    <div className={`p-2 rounded-md ${currentLayout === option.value ? "bg-primary/10" : "bg-muted"}`}>
                      {option.icon}
                    </div>
                    <span className="text-xs font-medium">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Card Style Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Style de la carte</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {cardStyleOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => isEditable && handlers.onCardStyleChange(group.id, group.layout_options, option.value)}
                    disabled={!isEditable || isPending}
                    className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                      currentCardStyle === option.value
                        ? "border-primary bg-primary/5"
                        : "border-muted hover:border-muted-foreground/50"
                    } ${!isEditable ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    <div className={`p-2 rounded-md ${currentCardStyle === option.value ? "bg-primary/10" : "bg-muted"}`}>
                      {option.icon}
                    </div>
                    <span className="text-xs font-medium">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Toggle Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-3">
                  {collapsedByDefault ? (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                  <div>
                    <Label className="text-sm font-medium">Replié par défaut</Label>
                    <p className="text-xs text-muted-foreground">
                      Le groupe sera fermé au chargement
                    </p>
                  </div>
                </div>
                <Switch
                  checked={collapsedByDefault}
                  onCheckedChange={(checked) => handlers.onCollapsedChange(group.id, group.layout_options, checked)}
                  disabled={!isEditable || isPending}
                />
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-3">
                  {showHeader ? (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  )}
                  <div>
                    <Label className="text-sm font-medium">Afficher l&apos;en-tête</Label>
                    <p className="text-xs text-muted-foreground">
                      Titre et description du groupe
                    </p>
                  </div>
                </div>
                <Switch
                  checked={showHeader}
                  onCheckedChange={(checked) => handlers.onShowHeaderChange(group.id, group.layout_options, checked)}
                  disabled={!isEditable || isPending}
                />
              </div>

              {/* Full Width Option - Only shown when multiple groups per row */}
              {showFullWidthOption && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 md:col-span-2">
                  <div className="flex items-center gap-3">
                    <Maximize2 className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <Label className="text-sm font-medium">Pleine largeur</Label>
                      <p className="text-xs text-muted-foreground">
                        Force le groupe à prendre toute la ligne
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={fullWidth}
                    onCheckedChange={(checked) => handlers.onFullWidthChange(group.id, group.layout_options, checked)}
                    disabled={!isEditable || isPending}
                  />
                </div>
              )}
            </div>

            {/* Hide Parent Name - Only shown for child output groups */}
            {type === "output" && isChildGroup && handlers.onHideParentNameChange && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 md:col-span-2">
                <div className="flex items-center gap-3">
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <Label className="text-sm font-medium">Masquer le nom du groupe parent</Label>
                    <p className="text-xs text-muted-foreground">
                      Affiche uniquement le nom du groupe enfant au lieu de &quot;Parent &gt; Enfant&quot;
                    </p>
                  </div>
                </div>
                <Switch
                  checked={hideParentName}
                  onCheckedChange={(checked) => handlers.onHideParentNameChange!(group.id, group.layout_options, checked)}
                  disabled={!isEditable || isPending}
                />
              </div>
            )}

            {/* Children Layout - Only shown when group has children */}
            {hasChildren && (
              <div className="space-y-3">
                <Label className="text-sm font-medium">Disposition des groupes enfants</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Ce groupe contient {group.children.length} groupe{group.children.length > 1 ? "s" : ""} enfant{group.children.length > 1 ? "s" : ""}. Choisissez comment les disposer.
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {childrenPerRowOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => isEditable && handlers.onChildrenPerRowChange(group.id, group.layout_options, option.value)}
                      disabled={!isEditable || isPending}
                      className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                        currentChildrenPerRow === option.value
                          ? "border-primary bg-primary/5"
                          : "border-muted hover:border-muted-foreground/50"
                      } ${!isEditable ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                    >
                      <div className={`flex gap-1 ${currentChildrenPerRow === option.value ? "text-primary" : "text-muted-foreground"}`}>
                        {Array.from({ length: option.value }).map((_, i) => (
                          <div key={i} className="w-5 h-6 rounded bg-current opacity-30" />
                        ))}
                      </div>
                      <span className="text-xs font-medium">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Radio Layout - Only shown for form groups containing at least one radio field */}
            {type === "form" && handlers.onRadioLayoutChange && (() => {
              const radioFieldsCount = (group.fields as FormField[]).filter((f) => f.field_type === "radio").length;
              if (radioFieldsCount === 0) return null;
              const currentRadioLayout = layoutOption.radio_layout;
              const onRadioLayoutChange = handlers.onRadioLayoutChange;
              return (
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Disposition des boutons radio</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Ce groupe contient {radioFieldsCount} champ{radioFieldsCount > 1 ? "s" : ""} radio. Choisissez la disposition de leurs options.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {radioLayoutOptions.map((option) => {
                      const isActive = currentRadioLayout === option.value;
                      return (
                        <button
                          key={option.label}
                          onClick={() => isEditable && onRadioLayoutChange(group.id, group.layout_options, option.value)}
                          disabled={!isEditable || isPending}
                          title={option.description}
                          className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                            isActive
                              ? "border-primary bg-primary/5"
                              : "border-muted hover:border-muted-foreground/50"
                          } ${!isEditable ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                        >
                          <div className={`p-2 rounded-md ${isActive ? "bg-primary/10" : "bg-muted"}`}>
                            {option.icon}
                          </div>
                          <span className="text-xs font-medium">{option.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Preview */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Aperçu</Label>
              <div 
                className={`p-4 rounded-lg border-2 border-dashed ${
                  currentCardStyle === "bordered" ? "border-border" :
                  currentCardStyle === "elevated" ? "shadow-md border-transparent" :
                  currentCardStyle === "minimal" ? "border-transparent bg-transparent" :
                  "border-muted"
                }`}
                style={layoutOption.background_color ? { backgroundColor: layoutOption.background_color } : undefined}
              >
                {showHeader && (
                  <div className="mb-3 pb-3 border-b flex items-center gap-2">
                    <IconComponent className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <h4 className="font-medium">{group.name}</h4>
                      {group.description && (
                        <p className="text-sm text-muted-foreground">{group.description}</p>
                      )}
                    </div>
                  </div>
                )}
                <div className={`gap-4 ${
                  currentLayout === "horizontal" ? "flex flex-wrap" :
                  currentLayout === "grid-2" ? "grid grid-cols-2" :
                  currentLayout === "grid-3" ? "grid grid-cols-3" :
                  currentLayout === "grid-4" ? "grid grid-cols-4" :
                  currentLayout === "grid-5" ? "grid grid-cols-5" :
                  "flex flex-col"
                }`}>
                  {fields.slice(0, 4).map((field) => (
                    <div 
                      key={field.id} 
                      className={`h-10 bg-muted rounded flex items-center px-3 text-sm text-muted-foreground ${
                        currentLayout === "horizontal" ? "flex-1 min-w-[150px]" : ""
                      }`}
                    >
                      {field.label}
                    </div>
                  ))}
                  {fields.length > 4 && (
                    <div className="h-10 bg-muted/50 rounded flex items-center justify-center px-3 text-sm text-muted-foreground">
                      +{fields.length - 4} autres {type === "form" ? "champs" : "calculs"}
                    </div>
                  )}
                  {fields.length === 0 && (
                    <div className="h-10 bg-muted/50 rounded flex items-center justify-center px-3 text-sm text-muted-foreground italic">
                      Aucun {type === "form" ? "champ" : "calcul"} dans ce groupe
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>
        </AccordionContent>
      </AccordionItem>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header with view toggle */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                Personnalisation de l&apos;apparence
              </CardTitle>
              <CardDescription>
                Configurez la mise en page et le style visuel de chaque groupe de champs du formulaire et des résultats.
              </CardDescription>
            </div>
            
            {/* View toggle */}
            <Tabs value={activeView} onValueChange={(v) => setActiveView(v as "config" | "preview")} className="w-auto">
              <TabsList className="grid grid-cols-2 w-[240px]">
                <TabsTrigger value="config" className="gap-2">
                  <Settings2 className="h-4 w-4" />
                  Configuration
                </TabsTrigger>
                <TabsTrigger value="preview" className="gap-2">
                  <Eye className="h-4 w-4" />
                  Aperçu
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
      </Card>

      {/* Preview Mode */}
      {activeView === "preview" && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          <SimulatorPreview
            formFieldGroups={formFieldGroups || []}
            outputFieldGroups={outputFieldGroups || []}
            appearanceOptions={appearanceOptions}
          />
        </motion.div>
      )}

      {/* Configuration Mode */}
      {activeView === "config" && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="space-y-6"
        >
          {/* Global Layout Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <LayoutGrid className="h-4 w-4" />
                Mise en page globale
              </CardTitle>
              <CardDescription>
                Configurez la disposition générale du formulaire et des résultats.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Form groups per row */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Groupes de formulaire par ligne</Label>
                <div className="grid grid-cols-3 gap-2">
                  {formGroupsPerRowOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => isEditable && handleFormGroupsPerRowChange(option.value)}
                      disabled={!isEditable || updateAppearanceMutation.isPending}
                      className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                        currentFormGroupsPerRow === option.value
                          ? "border-primary bg-primary/5"
                          : "border-muted hover:border-muted-foreground/50"
                      } ${!isEditable ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                    >
                      <div className={`flex gap-1 ${currentFormGroupsPerRow === option.value ? "text-primary" : "text-muted-foreground"}`}>
                        {Array.from({ length: option.value }).map((_, i) => (
                          <div key={i} className="w-6 h-8 rounded bg-current opacity-30" />
                        ))}
                      </div>
                      <span className="text-xs font-medium">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Output groups per row */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Groupes de résultats par ligne</Label>
                <div className="grid grid-cols-3 gap-2">
                  {outputGroupsPerRowOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => isEditable && handleOutputGroupsPerRowChange(option.value)}
                      disabled={!isEditable || updateAppearanceMutation.isPending}
                      className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                        currentOutputGroupsPerRow === option.value
                          ? "border-primary bg-primary/5"
                          : "border-muted hover:border-muted-foreground/50"
                      } ${!isEditable ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                    >
                      <div className={`flex gap-1 ${currentOutputGroupsPerRow === option.value ? "text-success" : "text-muted-foreground"}`}>
                        {Array.from({ length: option.value }).map((_, i) => (
                          <div key={i} className="w-6 h-8 rounded bg-current opacity-30" />
                        ))}
                      </div>
                      <span className="text-xs font-medium">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Results position */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Position des résultats</Label>
                <div className="grid grid-cols-2 gap-2">
                  {resultsPositionOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => isEditable && handleResultsPositionChange(option.value)}
                      disabled={!isEditable || updateAppearanceMutation.isPending}
                      className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                        currentResultsPosition === option.value
                          ? "border-primary bg-primary/5"
                          : "border-muted hover:border-muted-foreground/50"
                      } ${!isEditable ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                    >
                      <div className={`flex ${option.value === "right" ? "flex-row" : "flex-col"} gap-1 ${
                        currentResultsPosition === option.value ? "text-primary" : "text-muted-foreground"
                      }`}>
                        <div className="w-10 h-8 rounded bg-current opacity-30" />
                        <div className={`${option.value === "right" ? "w-6 h-8" : "w-10 h-4"} rounded bg-success opacity-50`} />
                      </div>
                      <div className="text-center">
                        <span className="text-sm font-medium block">{option.label}</span>
                        <span className="text-xs text-muted-foreground">{option.description}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Results width (only when position is right) */}
              {currentResultsPosition === "right" && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Largeur des résultats</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {resultsWidthOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => isEditable && handleResultsWidthChange(option.value)}
                        disabled={!isEditable || updateAppearanceMutation.isPending}
                        className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                          currentResultsWidth === option.value
                            ? "border-primary bg-primary/5"
                            : "border-muted hover:border-muted-foreground/50"
                        } ${!isEditable ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                      >
                        <div className={`flex gap-0.5 w-full justify-center ${
                          currentResultsWidth === option.value ? "text-primary" : "text-muted-foreground"
                        }`}>
                          <div className={`h-6 rounded bg-current opacity-30 ${
                            option.value === "third" ? "flex-[2]" : option.value === "half" ? "flex-1" : "flex-0 w-0"
                          }`} />
                          <div className={`h-6 rounded bg-success opacity-50 ${
                            option.value === "third" ? "flex-1" : option.value === "half" ? "flex-1" : "flex-1"
                          }`} />
                        </div>
                        <span className="text-xs font-medium">{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Attachments position (only shown when attachments are enabled) */}
              {attachmentsEnabled && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Position des pièces jointes</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {attachmentsPositionOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => isEditable && handleAttachmentsPositionChange(option.value)}
                        disabled={!isEditable || updateAppearanceMutation.isPending}
                        className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                          currentAttachmentsPosition === option.value
                            ? "border-primary bg-primary/5"
                            : "border-muted hover:border-muted-foreground/50"
                        } ${!isEditable ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                      >
                        <div className={`flex ${option.value === "bottom" ? "flex-col" : "flex-row"} gap-1 ${
                          currentAttachmentsPosition === option.value ? "text-primary" : "text-muted-foreground"
                        }`}>
                          {option.value === "form" && (
                            <>
                              <div className="w-8 h-6 rounded bg-current opacity-30" />
                              <div className="w-4 h-6 rounded bg-warning opacity-50" />
                            </>
                          )}
                          {option.value === "results" && (
                            <>
                              <div className="w-8 h-6 rounded bg-success opacity-30" />
                              <div className="w-4 h-6 rounded bg-warning opacity-50" />
                            </>
                          )}
                          {option.value === "bottom" && (
                            <>
                              <div className="flex gap-1">
                                <div className="w-6 h-4 rounded bg-current opacity-30" />
                                <div className="w-6 h-4 rounded bg-success opacity-30" />
                              </div>
                              <div className="w-full h-3 rounded bg-warning opacity-50" />
                            </>
                          )}
                        </div>
                        <div className="text-center">
                          <span className="text-sm font-medium block">{option.label}</span>
                          <span className="text-xs text-muted-foreground">{option.description}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Définissez où la section pièces jointes apparaîtra dans le simulateur.
                  </p>
                </div>
              )}

              {/* Show output descriptions toggle */}
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium">Afficher les descriptions des résultats</Label>
                  <p className="text-xs text-muted-foreground">
                    Affiche les descriptions sous les labels des champs de calcul dans les résultats.
                  </p>
                </div>
                <Switch
                  checked={currentShowOutputDescriptions}
                  onCheckedChange={handleShowOutputDescriptionsChange}
                  disabled={!isEditable || updateAppearanceMutation.isPending}
                />
              </div>

              {/* Show spinbox toggle */}
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium">Afficher les boutons de navigation numérique (spinbox)</Label>
                  <p className="text-xs text-muted-foreground">
                    Affiche les flèches haut/bas sur les champs de type nombre.
                  </p>
                </div>
                <Switch
                  checked={currentShowSpinbox}
                  onCheckedChange={handleShowSpinboxChange}
                  disabled={!isEditable || updateAppearanceMutation.isPending}
                />
              </div>
            </CardContent>
          </Card>

          {/* Form Groups Configuration */}
          {flattenedFormGroups.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Settings2 className="h-4 w-4" />
                  Configuration des groupes de formulaire
                </CardTitle>
                <CardDescription>
                  Personnalisez l&apos;affichage des champs de saisie.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="multiple" className="space-y-2">
                  {flattenedFormGroups.map((group) =>
                    renderGroupConfig(
                      group,
                      "form",
                      {
                        onLayoutChange: handleFormLayoutChange,
                        onCardStyleChange: handleFormCardStyleChange,
                        onCollapsedChange: handleFormCollapsedChange,
                        onShowHeaderChange: handleFormShowHeaderChange,
                        onIconChange: handleFormIconChange,
                        onFullWidthChange: handleFormFullWidthChange,
                        onChildrenPerRowChange: handleFormChildrenPerRowChange,
                        onBackgroundColorChange: handleFormBackgroundColorChange,
                        onRadioLayoutChange: handleFormRadioLayoutChange,
                      },
                      updateFormGroupMutation.isPending
                    )
                  )}
                </Accordion>
              </CardContent>
            </Card>
          )}

          {/* Output Groups Configuration */}
          {flattenedOutputGroups.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Calculator className="h-4 w-4" />
                  Configuration des groupes de résultats
                </CardTitle>
                <CardDescription>
                  Personnalisez l&apos;affichage des calculs et résultats.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="multiple" className="space-y-2">
                  {flattenedOutputGroups.map((group) =>
                    renderGroupConfig(
                      group,
                      "output",
                      {
                        onLayoutChange: handleOutputLayoutChange,
                        onCardStyleChange: handleOutputCardStyleChange,
                        onCollapsedChange: handleOutputCollapsedChange,
                        onShowHeaderChange: handleOutputShowHeaderChange,
                        onIconChange: handleOutputIconChange,
                        onFullWidthChange: handleOutputFullWidthChange,
                        onChildrenPerRowChange: handleOutputChildrenPerRowChange,
                        onBackgroundColorChange: handleOutputBackgroundColorChange,
                        onHideParentNameChange: handleOutputHideParentNameChange,
                      },
                      updateOutputGroupMutation.isPending
                    )
                  )}
                </Accordion>
              </CardContent>
            </Card>
          )}
        </motion.div>
      )}
    </div>
  );
}
